/**
 * The attention queue, derived from platform state the backend already
 * holds. Each rule turns one observable condition into a case with evidence
 * and a suggested action. There is no case store yet: cases appear while the
 * condition holds and disappear when it clears (docs/product-interfaces.md).
 */
import type { AttentionCase, DeviceDetail, DeviceSummary, OrderSummary, Organisation, PlatformHealth } from './model';
import type { Severity } from './status';
import { formatAgoShort } from './format';

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

export interface AttentionInput {
  now: Date;
  devices: (DeviceSummary & Partial<Pick<DeviceDetail, 'openGaps' | 'generationNo' | 'configInSync' | 'runtime'>>)[];
  orders: OrderSummary[];
  organisations: Organisation[];
  health: PlatformHealth | null;
}

const SEVERITY_ORDER: Record<Severity, number> = { critical: 0, warning: 1, info: 2 };

export function deriveAttention({ now, devices, orders, organisations, health }: AttentionInput): AttentionCase[] {
  const cases: AttentionCase[] = [];
  const age = (iso: string) => now.getTime() - new Date(iso).getTime();
  const add = (c: Omit<AttentionCase, 'status'>) => cases.push({ ...c, status: 'Open' });
  const orgName = (o: OrderSummary) =>
    organisations.find((x) => x.id === o.organisation.id)?.name ?? o.organisation.slug;

  if (health && (health.ingestion === false || health.database === false)) {
    add({
      id: 'platform-ingestion',
      severity: 'critical',
      type: 'Data ingestion',
      title:
        health.database === false
          ? 'The platform database is not reachable'
          : 'Ingestion is disconnected from the device broker',
      organisation: null,
      device: null,
      order: null,
      firstSeenAt: now.toISOString(),
      evidence: [
        `Readiness: database ${health.database ? 'ok' : 'down'}, device broker ${health.ingestion ? 'ok' : 'disconnected'}`,
        'Devices keep measuring and buffer up to 48 hours; nothing is lost while the buffer holds.',
      ],
      suggestedAction:
        'Check the backend and broker containers. Devices deliver their buffer once ingestion reconnects; confirm with the gap list afterwards.',
    });
  }

  for (const d of devices) {
    const ref = { id: d.id, serial: d.serial };
    const org = d.organisation?.name ?? null;
    if (d.lifecycle === 'offline') {
      add({
        id: `offline-${d.id}`,
        severity: 'critical',
        type: 'Device offline',
        title: `${d.serial} stopped reporting`,
        organisation: org,
        device: ref,
        order: null,
        firstSeenAt: d.lastSeenAt ?? now.toISOString(),
        evidence: [
          `Last seen ${formatAgoShort(d.lastSeenAt, now)} ago`,
          d.link.kind === 'lte-m'
            ? `Last signal RSRP ${d.link.rsrpDbm} dBm on ${d.link.operator}`
            : 'No cellular diagnostics for this hardware',
        ],
        suggestedAction:
          'Most often the meter cupboard lost power or the device was unplugged. If it stays away for more than 24 hours, contact the organisation; the device delivers its buffered readings when it returns.',
      });
    }
    if (d.online && d.data === 'delayed') {
      add({
        id: `data-${d.id}`,
        severity: 'warning',
        type: 'Meter data',
        title: `${d.serial} is connected but sends no meter data`,
        organisation: org,
        device: ref,
        order: null,
        firstSeenAt: d.lastSeenAt ?? now.toISOString(),
        evidence: [
          'The device reports state, but its latest reading is older than three reporting intervals.',
          d.runtime?.meter.connected === false
            ? 'The device reports that the P1 port gives no telegrams.'
            : 'P1 port state unknown.',
        ],
        suggestedAction:
          'Request the device state. If the P1 port is silent, the cable is loose or the meter port is disabled by the grid operator.',
      });
    }
    if (d.openGaps && d.openGaps.length > 0) {
      const missing = d.openGaps.reduce((n, g) => n + (g.toSeq - g.fromSeq + 1), 0);
      add({
        id: `gap-${d.id}`,
        severity: 'warning',
        type: 'Meter data',
        title: `${missing} readings missing from ${d.serial}`,
        organisation: org,
        device: ref,
        order: null,
        firstSeenAt: d.openGaps[0]!.detectedAt,
        evidence: d.openGaps.slice(0, 3).map((g) => `Sequence ${g.fromSeq}–${g.toSeq} not received`),
        suggestedAction:
          'Send a backfill command; the device re-sends the range from its buffer. The gap closes by itself when the records arrive.',
      });
    }
    if (d.generationNo !== undefined && d.generationNo > 1) {
      add({
        id: `reset-${d.id}`,
        severity: 'info',
        type: 'Device state',
        title: `${d.serial} lost its stored state ${d.generationNo - 1}×`,
        organisation: org,
        device: ref,
        order: null,
        firstSeenAt: d.lastSeenAt ?? now.toISOString(),
        evidence: [`${d.generationNo} sequence generations recorded`, 'Readings buffered before a reset are gone.'],
        suggestedAction:
          'Repeated resets point to storage or power trouble. Compare the reset reasons in the event log; replace the device if they continue.',
      });
    }
    if (d.runtime?.retentionSatisfied === false) {
      add({
        id: `buffer-${d.id}`,
        severity: 'info',
        type: 'Device state',
        title: `${d.serial} cannot buffer the required offline period`,
        organisation: org,
        device: ref,
        order: null,
        firstSeenAt: d.runtime.reportedAt,
        evidence: ['The device reports less buffer retention than configured.'],
        suggestedAction: 'Check free storage in the diagnostics. Until fixed, a long outage loses the oldest readings.',
      });
    }
  }

  for (const o of orders) {
    const ref = { id: o.id, reference: o.reference };
    const device = o.device;
    if (o.stage === 'delivered' && o.shipment?.deliveredAt && age(o.shipment.deliveredAt) > 2 * DAY) {
      add({
        id: `not-connected-${o.id}`,
        severity: 'warning',
        type: 'Activation',
        title: `Delivered ${formatAgoShort(o.shipment.deliveredAt, now)} ago, never connected`,
        organisation: orgName(o),
        device,
        order: ref,
        firstSeenAt: o.shipment.deliveredAt,
        evidence: [
          `Order ${o.reference} for ${o.recipient.name}`,
          'The device has not been seen since it left the warehouse.',
        ],
        suggestedAction:
          'Ask the organisation to remind the customer to plug it in. If it is plugged in, the meter port may be closed or there is no coverage in the meter cupboard.',
      });
    }
    if (o.stage === 'shipped' && o.shipment?.shippedAt && age(o.shipment.shippedAt) > 5 * DAY) {
      add({
        id: `in-transit-${o.id}`,
        severity: 'warning',
        type: 'Fulfilment',
        title: `In transit for ${formatAgoShort(o.shipment.shippedAt, now)}`,
        organisation: orgName(o),
        device,
        order: ref,
        firstSeenAt: o.shipment.shippedAt,
        evidence: [`Tracking ${o.shipment.trackingCode ?? 'unknown'} with ${o.shipment.carrier}`],
        suggestedAction:
          'Check the carrier. If the parcel is lost, replace the device on the order so the customer keeps the same meter point.',
      });
    }
    if (o.stage === 'awaiting_device' && age(o.createdAt) > DAY) {
      add({
        id: `backlog-${o.id}`,
        severity: 'info',
        type: 'Fulfilment',
        title: `Waiting for a device for ${formatAgoShort(o.createdAt, now)}`,
        organisation: orgName(o),
        device: null,
        order: ref,
        firstSeenAt: o.createdAt,
        evidence: [`Order ${o.reference} has no device assigned`],
        suggestedAction: 'Pick and pack it in Fulfilment.',
      });
    }
  }

  for (const org of organisations) {
    const w = org.webhook;
    if (w.configured && (w.stalled || !w.healthy)) {
      add({
        id: `webhook-${org.id}`,
        severity: w.stalled ? 'critical' : 'warning',
        type: 'Webhook delivery',
        title: w.stalled ? `Deliveries to ${org.name} have stopped` : `Deliveries to ${org.name} are failing`,
        organisation: org.name,
        device: null,
        order: null,
        firstSeenAt: w.lastFailureAt ?? now.toISOString(),
        evidence: [
          `${w.pending} events waiting`,
          w.lastError ? `Last error: ${w.lastError}` : 'No error text recorded',
          `Last success ${formatAgoShort(w.lastSuccessAt, now)} ago`,
        ],
        suggestedAction: w.stalled
          ? 'Delivery gave up after the maximum attempts. Contact the organisation; once their endpoint answers, re-queue the deliveries. Events stay in their log and can be read with the cursor API meanwhile.'
          : 'Retries continue automatically with backoff. Contact the organisation if it does not recover.',
      });
    }
  }

  return cases.sort(
    (a, b) =>
      SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] ||
      new Date(a.firstSeenAt).getTime() - new Date(b.firstSeenAt).getTime(),
  );
}
